import { JSX, splitProps, For, Show, createSignal, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { ArrowDownIcon } from './icons';
import { Text } from './text';

interface SelectProps {
    options: { value: string; label: string; icon?: JSX.Element }[];
    value: string;
    onChange: (e: { currentTarget: { value: string } }) => void;
    placeholder?: string;
    error?: string;
    class?: string;
    disabled?: boolean;
}

export const Select = (props: SelectProps) => {
    const [local, others] = splitProps(props, ['class', 'options', 'placeholder', 'error', 'value', 'onChange', 'disabled']);
    const [isOpen, setIsOpen] = createSignal(false);
    // The dropdown renders in a Portal (so it escapes overflow-hidden/scroll
    // ancestors like table cells) and is positioned with `fixed` from the
    // trigger's viewport rect.
    const [menuPos, setMenuPos] = createSignal({ top: 0, bottom: 0, left: 0, right: 0, width: 0, dropUp: false, alignRight: false });
    let containerRef: HTMLDivElement | undefined;
    let buttonRef: HTMLButtonElement | undefined;
    let menuRef: HTMLDivElement | undefined;

    const selectedOption = () => local.options.find((opt) => opt.value === local.value);
    const displayValue = () => selectedOption()?.label || local.placeholder || 'Select...';

    const updatePosition = () => {
        if (!buttonRef) return;
        const r = buttonRef.getBoundingClientRect();
        // Flip the menu upward when it would spill past the viewport bottom and
        // there's more room above (e.g. a Select in a bottom toolbar). Anchor by
        // `bottom` when dropping up so it grows from the trigger regardless of
        // height. Uses the real menu height once rendered; estimates before.
        const menuH = menuRef?.offsetHeight || 240;
        const spaceBelow = window.innerHeight - r.bottom;
        const dropUp = spaceBelow < menuH && r.top > spaceBelow;
        // Same idea horizontally: when a wider-than-trigger menu (w-max) would
        // spill past the viewport's right edge, align its right edge to the
        // trigger's right edge instead of its left.
        const menuW = menuRef?.offsetWidth || r.width;
        const alignRight = r.left + menuW > window.innerWidth;
        setMenuPos({ top: r.bottom, bottom: window.innerHeight - r.top, left: r.left, right: window.innerWidth - r.right, width: r.width, dropUp, alignRight });
    };

    const open = () => {
        setIsOpen(true);
        updatePosition();
    };

    const handleSelect = (value: string) => {
        local.onChange({ currentTarget: { value } });
        setIsOpen(false);
    };

    // Close on any click that lands outside both the trigger and the portalled
    // menu (the menu isn't a DOM descendant of the container).
    const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (containerRef?.contains(target) || menuRef?.contains(target)) return;
        setIsOpen(false);
    };

    // Keep the fixed menu glued to the trigger while a scrolling ancestor or the
    // window moves. Capture-phase catches scrolls on inner containers too.
    const handleReposition = () => {
        if (isOpen()) updatePosition();
    };

    onMount(() => {
        document.addEventListener('click', handleClickOutside);
        window.addEventListener('scroll', handleReposition, true);
        window.addEventListener('resize', handleReposition);
    });

    onCleanup(() => {
        document.removeEventListener('click', handleClickOutside);
        window.removeEventListener('scroll', handleReposition, true);
        window.removeEventListener('resize', handleReposition);
    });

    return (
        <div ref={containerRef} class={`relative ${local.class || ''}`} onClick={(e) => e.stopPropagation()}>
            <button
                ref={buttonRef}
                type='button'
                class={`w-full px-2 h-7 flex items-center justify-between cursor-default text-sm rounded gap-1 ${isOpen() ? 'bg-muted-foreground/20' : 'bg-transparent hover:bg-muted'} ${local.error ? 'border-red' : ''} ${local.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={(e) => {
                    e.stopPropagation();
                    if (local.disabled) return;
                    if (isOpen()) setIsOpen(false);
                    else open();
                }}
                disabled={local.disabled}>
                <div class='flex items-center gap-1'>
                    {selectedOption()?.icon && selectedOption()?.icon}
                    <Text tone={!selectedOption() && local.placeholder ? 'muted' : 'foreground'}>{displayValue()}</Text>
                </div>
                <ArrowDownIcon open={isOpen} />
            </button>
            <Show when={isOpen()}>
                <Portal>
                    <div
                        ref={menuRef}
                        class='fixed w-max max-w-[20rem] bg-background border rounded shadow z-[100] max-h-96 overflow-y-auto scrollbar-hide backdrop-blur-lg p-1'
                        style={{ ...(menuPos().dropUp ? { bottom: `${menuPos().bottom}px` } : { top: `${menuPos().top}px` }), ...(menuPos().alignRight ? { right: `${menuPos().right}px` } : { left: `${menuPos().left}px` }), 'min-width': `${menuPos().width}px` }}
                        onClick={(e) => e.stopPropagation()}>
                        <For each={local.options}>
                            {(option) => {
                                const isSelected = () => option.value === local.value;
                                return (
                                    <button
                                        type='button'
                                        class={`w-full min-w-0 px-2 py-1 text-sm text-left rounded cursor-default flex items-center gap-1 hover:bg-muted ${isSelected() ? 'bg-muted-foreground/20' : ''} text-foreground`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelect(option.value);
                                        }}>
                                        {option.icon && option.icon}
                                        <Text class='truncate'>{option.label}</Text>
                                    </button>
                                );
                            }}
                        </For>
                    </div>
                </Portal>
            </Show>
        </div>
    );
};
